import { Controller } from "@hotwired/stimulus"
import { requestConfirm, pluralize } from "../util"

// Connects to data-controller="manage-contents-actions"
export default class extends Controller {
  static targets = ["form", "option", "hint", "confirmButton"]
  static values = {
    contentsCount: String,
    publishedCount: String,
    unpublishedCount: String,
    archivedCount: String,
  }

  TYPES = {
    publish: "publish",
    unpublish: "unpublish",
    download: "download",
    archive: "archive",
    unarchive: "unarchive",
    delete: "delete"
  }

  connect() {
    this.checkHintsVisibility()
  }

  checkHintsVisibility() {
    this.updateHintVisibility({
      condition: this.publishableCount() === 0,
      actionName: this.TYPES.publish,
      message: +this.publishedCountValue === +this.contentsCountValue
        ? 'All items are already published.'
        : 'Archived content cannot be published.'
    })
    this.updateHintVisibility({
      condition: this.contentsCountValue === this.unpublishedCountValue,
      actionName: this.TYPES.unpublish,
      message: 'All items are already unpublished.'
    })
    this.updateHintVisibility({
      condition: this.hasTextItems(),
      actionName: this.TYPES.download,
      message: 'Text items cannot be downloaded.'
    })
    this.updateHintVisibility({
      condition: this.archivableCount() === 0,
      actionName: this.TYPES.archive,
      message: +this.archivedCountValue === +this.contentsCountValue
        ? 'All items are already archived.'
        : 'Published items cannot be archived.'
    })
    this.updateHintVisibility({
      condition: +this.archivedCountValue === 0,
      actionName: this.TYPES.unarchive,
      message: 'No items are archived.'
    })
    this.updateHintVisibility({
      condition: this.hasPublishedItems(),
      actionName: this.TYPES.delete,
      message: 'Published posts cannot be deleted. Unpublish the post first.'
    })
  }

  archivableCount() {
    return +this.contentsCountValue - +this.publishedCountValue - +this.archivedCountValue
  }

  publishableCount() {
    const count = +this.contentsCountValue - +this.publishedCountValue - +this.archivedCountValue
    return Math.max(count, 0)
  }

  updateHintVisibility({ condition, actionName, message }) {
    if (!condition) return

    const option = this.findOptionByActionName(actionName)
    if (!option) return

    const hint = option.querySelector('[data-manage-contents-actions-target="hint"]')
    hint.dataset.show = true
    hint.innerHTML = message
    hint.classList.remove("hidden!")
    option.querySelector('input[type="radio"]').disabled = true
  }

  findOptionByActionName(actionName) {
    return this.optionTargets.find(option =>
      option.querySelector('input[type="radio"]').value === actionName
    )
  }

  canSubmitForm() {
    const type = this.getSelectedActionType()
    const isValid = this.validateAction(type)
    this.confirmButtonTarget.disabled = !isValid
  }

  validateAction(type) {
    switch (type) {
      case this.TYPES.publish:
        return this.checkPublishAvailability()
      case this.TYPES.unpublish:
        return this.checkUnpublishAvailability()
      case this.TYPES.archive:
        return this.checkArchiveAvailability()
      case this.TYPES.unarchive:
        return this.checkUnarchiveAvailability()
      case this.TYPES.download:
      case this.TYPES.delete:
        return true
      default:
        throw new Error("Unknown action")
    }
  }

  checkPublishAvailability() {
    const selectedOption = this.getSelectedOption()
    const hint = this.hintTargets[this.optionTargets.indexOf(selectedOption)]
    const publishable = this.publishableCount()

    if (publishable === 0) {
      hint.innerHTML = +this.publishedCountValue === +this.contentsCountValue
        ? 'All items are already published.'
        : 'Archived content cannot be published.'
      hint.classList.remove("hidden!")
      return false
    }

    if (publishable < +this.contentsCountValue) {
      const skipped = +this.contentsCountValue - publishable
      hint.innerHTML = `${pluralize(skipped, 'item is', 'items are')} already archived or published.<br>Do you want to publish the rest?`
      hint.classList.remove("hidden!")
    }

    return true
  }

  checkUnpublishAvailability() {
    return this.checkAvailability({
      countValue: this.publishedCountValue,
      oppositeCountValue: this.unpublishedCountValue,
      allMessage: 'All items are already unpublished.',
      partialMessage: 'already unpublished.<br>Do you want to unpublish the rest?'
    })
  }

  checkArchiveAvailability() {
    const selectedOption = this.getSelectedOption()
    const hint = this.hintTargets[this.optionTargets.indexOf(selectedOption)]
    const archivable = this.archivableCount()

    if (archivable === 0) {
      hint.innerHTML = +this.archivedCountValue === +this.contentsCountValue
        ? 'All items are already archived.'
        : 'Published items cannot be archived.'
      hint.classList.remove("hidden!")
      return false
    }

    if (archivable < +this.contentsCountValue) {
      const skipped = +this.contentsCountValue - archivable
      hint.innerHTML = `${pluralize(skipped, 'item is', 'items are')} already archived or published.<br>Do you want to archive the rest?`
      hint.classList.remove("hidden!")
    }

    return true
  }

  checkUnarchiveAvailability() {
    const nonArchivedCount = String(+this.contentsCountValue - +this.archivedCountValue)

    return this.checkAvailability({
      countValue: this.archivedCountValue,
      oppositeCountValue: nonArchivedCount,
      allMessage: 'No items are archived.',
      partialMessage: 'not archived.<br>Do you want to unarchive the rest?'
    })
  }

  checkAvailability({ countValue, oppositeCountValue, allMessage, partialMessage }) {
    const selectedOption = this.getSelectedOption()
    const optionIndex = this.optionTargets.indexOf(selectedOption)
    const hint = this.hintTargets[optionIndex]

    if (countValue === this.contentsCountValue) {
      return true
    } else if (oppositeCountValue === this.contentsCountValue) {
      hint.innerHTML = allMessage
    } else {
      hint.innerHTML = `${pluralize(+oppositeCountValue, 'item is', 'items are')} ${partialMessage}`
    }

    hint.classList.remove("hidden!")
    return oppositeCountValue !== this.contentsCountValue
  }

  confirm(event) {
    event.preventDefault()
    const type = this.getSelectedActionType()

    const actions = {
      [this.TYPES.publish]: () => this.confirmPublish(),
      [this.TYPES.unpublish]: () => this.confirmUnpublish(),
      [this.TYPES.download]: () => this.confirmDownload(),
      [this.TYPES.archive]: () => this.confirmArchive(),
      [this.TYPES.unarchive]: () => this.confirmUnarchive(),
      [this.TYPES.delete]: () => this.confirmDelete()
    }

    if (!actions[type]) {
      throw new Error("Unknown action")
    }

    actions[type]()
  }

  getSelectedActionType() {
    const selectedOption = this.getSelectedOption()
    return selectedOption.querySelector('input[type="radio"]').value
  }

  getSelectedOption() {
    return this.optionTargets.find(option => option.querySelector('input[type="radio"]').checked)
  }

  confirmPublish() {
    this.confirmAction({
      modalType: "confirm-publish-contents",
      title: `Publish ${this.getConfirmModalItemsCountText(this.publishableCount())}`
    })
  }

  confirmUnpublish() {
    this.confirmAction({
      modalType: "confirm-unpublish-contents",
      title: `Unpublish ${this.getConfirmModalItemsCountText(this.publishedCountValue)}`
    })
  }

  confirmDownload() {
    this.confirmAction({
      modalType: "confirm-download-contents",
      title: `Download ${this.getConfirmModalItemsCountText(this.contentsCountValue)}`
    })
  }

  confirmArchive() {
    this.confirmAction({
      modalType: "confirm-archive-contents",
      title: `Archive ${this.getConfirmModalItemsCountText(this.archivableCount())}`,
      text: "Archived items will be hidden from the gallery."
    })
  }

  confirmUnarchive() {
    this.confirmAction({
      modalType: "confirm-unarchive-contents",
      title: `Unarchive ${this.getConfirmModalItemsCountText(this.archivedCountValue)}`,
      text: "Unarchived items will reappear in the gallery."
    })
  }

  confirmDelete() {
    this.confirmAction({
      modalType: "confirm-delete-contents",
      title: `Delete ${this.getConfirmModalItemsCountText(this.contentsCountValue)}`,
      text: "Are you sure you want to delete this post? This action will deduct the time and points the ambassador spent creating this content.<br><strong>To avoid deducting time and points you can archive post instead.</strong>",
      confirmButtonText: "Delete"
    })
  }

  getConfirmModalItemsCountText(count) {
    return `${count} ${+count > 1 ? "items" : "item"}`
  }

  async confirmAction({ modalType, title, text = "", confirmButtonText = "Confirm" }) {
    const modalData = { title, text, confirmButtonText }
    const confirmationResult = await requestConfirm({ modal: modalType, data: modalData })
    if (!confirmationResult) return

    this.formTarget.requestSubmit()
    this.dispatchEvent("drawer:close")
    this.dispatchEvent("table-checkboxes:clear")
    this.dispatchEvent("content-accordion:open")
  }

  dispatchEvent(eventName) {
    this.element.dispatchEvent(new CustomEvent(eventName, { bubbles: true }))
  }

  selectOption() {
    this.hintTargets.forEach(hint => {
      if (!hint.dataset.show) {
        hint.classList.add("hidden!")
      }
    })
    this.canSubmitForm()
  }

  hasTextItems() {
    const selectedCheckboxes = document.querySelectorAll('input[type="checkbox"]:checked')
    return Array.from(selectedCheckboxes).some(checkbox =>
      checkbox.value.startsWith("text_") || checkbox.id.startsWith("text_")
    )
  }

  hasPublishedItems() {
    return +this.publishedCountValue > 0
  }
}
